// components/FounderStorySection.js
import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Container } from "../styles/StyledComponents";
import { FadeInUp, SlideInLeft, SlideInRight } from "./ScrollAnimations";

const Section = styled.section`
  padding: 100px 2rem;
  background: #000000;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  gap: 80px;
  max-width: 1350px;
  margin: 0 auto;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const MainHeading = styled(motion.h2)`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  background: #ffffff;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PlusIcon = styled(motion.div)`
  width: auto;
  height: auto;
  border-radius: 50%;
  display: flex;
  font-size: 2rem;
  color: white;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const PlaceholderText = styled(motion.p)`
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
  color: #ffffff;
  display: flex;
  align-items: center;
`;

const StoryHeading = styled(motion.p)`
  font-size: 21px;
  font-weight: 500;
  line-height: 1.2;
  color: #ffffff;
  margin-bottom: 2rem;
`;

const FounderImage = styled(motion.div)`
  width: 100%;
  max-width: 400px;
  height: 400px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    height: auto;
    aspect-ratio: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StoryContent = styled(motion.div)`
  height: 100%;
`;

const StoryText = styled.p`
  font-size: 1.2rem;
  font-weight: 500;
  line-height: 1.7;
  color: #ffffff;
  margin-bottom: 1.5rem;
`;

const FounderStorySection = () => {
  return (
    <Section id="founder-story">
      <Container>
        <GridContainer>
          {/* Top Row */}
          <Row>
            <Column>
              <SlideInLeft>
                <MainHeading>
                  Meet Lizzy Reid, Founder Story
                </MainHeading>
              </SlideInLeft>
            </Column>

            <Column>
              <SlideInRight>
                <PlusIcon>+</PlusIcon>
                <PlaceholderText>
                  Lizzy is a global recruitment leader with over 15 years'
                  experience building high impact teams worldwide. With a career
                  spanning London, Paris, Shanghai to San Francisco she partners
                  with luxury, fashion, creative and digital and private equity
                  companies to connect visionary leaders with transformational
                  talent. Guided by values of integrity, collaboration, care and
                  commitment she brings people together.
                </PlaceholderText>
              </SlideInRight>
            </Column>
          </Row>

          {/* Bottom Row */}
          <Row>
            <Column style={{ justifyContent: "flex-end", display: "flex", height: "100%" }}>
              <FadeInUp>
                <FounderImage
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <img src="/lizzyPic.jpg" alt="Founder Lizzy" />
                </FounderImage>
              </FadeInUp>
            </Column>

            <Column>
              <FadeInUp>
                <StoryContent>
                  <StoryHeading>
                    A journey of vision & impact
                  </StoryHeading>

                  <StoryText>
                    Lizzy began her career in London, and shortly after working
                    in Shanghai during China's luxury boom as a Headhunter. She
                    expanded her practice across global markets. Known for
                    forging trusted relationships with C-suite executives,
                    senior leaders and HR teams, she combines a global
                    perspective with a highly personalized, hands-on approach to
                    recruitment.
                    <br />
                    <br />
                    Her expertise lies in identifying rising stars who drive
                    innovation, scale and culture, whilst mentoring and
                    consulting with future leaders. Recognized as one of Soho
                    Work's Future 50 (2023) and The Dots' Rising Stars (2021),
                    Lizzy continues to build lasting teams that shape industries
                    worldwide.
                  </StoryText>
                </StoryContent>
              </FadeInUp>
            </Column>
          </Row>
        </GridContainer>
      </Container>
    </Section>
  );
};

export default FounderStorySection;